// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import DynamicListInput from '../../components/Form/DynamicListInput';
import TextInput from '../../components/Form/TextInput';
import { PartyCredentialRequirementElement, ClaimRequirementElement } from './Common';
import RequiredClaimsInput from './RequiredClaimsInput';

interface CredentialsInputProps {
  list: PartyCredentialRequirementElement[];
  setList: React.Dispatch<React.SetStateAction<PartyCredentialRequirementElement[]>>;
  title: string;
}

const RequiredCredentialsInput: React.FC<CredentialsInputProps> = ({ list, setList, title }) => {
  // Helper to update the list
  const updateList = (
    id: string,
    updateFn: (item: PartyCredentialRequirementElement) => PartyCredentialRequirementElement,
  ) => {
    setList((prev) => prev.map((item) => (item.id === id ? updateFn(item) : item)));
  };

  // Rendering the dynamic input form for PartyCredentialRequirementElement.
  return (
    <DynamicListInput<PartyCredentialRequirementElement>
      list={list}
      setList={setList}
      title={title}
      createNewItem={() => ({
        id: uuidv4(),
        issuer: '',
        requiredClaims: [],
      })}
      renderItem={(credentialRequirement: PartyCredentialRequirementElement) => (
        <Box mb={3}>
          <TextInput
            label="Credential Issuer"
            value={credentialRequirement.issuer}
            setValue={(newValue: string | ((prevValue: string) => string)) =>
              updateList(credentialRequirement.id, (item) => ({
                ...item,
                issuer: typeof newValue === 'function' ? newValue(item.issuer) : newValue,
              }))
            }
            required
          />
          <Box ml={2} mt={1}>
            <RequiredClaimsInput
              claimList={credentialRequirement.requiredClaims}
              setClaimList={(
                newClaimList:
                  | ClaimRequirementElement[]
                  | ((prevClaims: ClaimRequirementElement[]) => ClaimRequirementElement[]),
              ) => {
                updateList(credentialRequirement.id, (item) => ({
                  ...item,
                  requiredClaims:
                    typeof newClaimList === 'function'
                      ? newClaimList(item.requiredClaims)
                      : newClaimList,
                }));
              }}
            />
          </Box>
        </Box>
      )}
    />
  );
};

export default RequiredCredentialsInput;
